import axios from "axios";
import Logger from "../logger";
import { setAuthConfig } from "../redux/reducers/sessionReducer";
import { baseContainer } from "../index";
import base from "./base";
import AuthenticationService from "./authenticationService";
import { Auth0TokenError, ApiAuthTokenError } from "../errors";
import { LoginStatus } from "../constants/loginStatus";

export default class Auth0AuthenticationService extends AuthenticationService {
  constructor() {
    super();
    this.auth0Instance = null;
    this.auth0Config = null;
  }

  setAppSetting(auth0Config, auth0Instance) {
    this.auth0Config = auth0Config;
    this.auth0Instance = auth0Instance;
  }

  async selectTenant(tenant) {
    this.changeStatus(LoginStatus.Pending);
    try {
      await this.extendLogInWithTenant(tenant.id);
      this.changeStatus(LoginStatus.Authorized);
    } catch (err) {
      this.changeStatus(LoginStatus.UnAuthorized);
    }
  }

  async login() {
     
    sessionStorage.removeItem("logout");

    try {
      // eslint-disable-next-line no-unused-vars
      let token = await this.getAccessToken();
    } catch (err) {
      if (
        ["login_required", "consent_required", "invalid_token", "interaction_required"].indexOf(
          err?.error
        ) >= 0
      )
        this.auth0Instance.loginWithRedirect();
    }
  }

  logout() {
    let config = baseContainer.getConfig();
    if (this.getTokenFromStorage()) {
      base.post({ url: config.apiEndpoints.identityLogout, data: config.appName }).then(() => {
        this.postLogout(true);
      });
    } else {
       
      window.location.href = "/";
    }
  }

  async getAuthToken() {
    Logger.of("App.AuthenticationService.getAuthToken").info();
    // Handle logout across tabs. If logged out, access token
    // will not exist, instead redirect to sign in page
    if (this.#isLoggingOut()) {
      this.#clearSessionAndLocalStorage();
       
      window.location.href = "/";
      return {};
    }

    let config = baseContainer.getConfig();
    Logger.of("App.AuthenticationService.getAuthToken").info({ config });

    // load session from redux store
    let session = baseContainer.getStoreState().session;
    Logger.of("App.AuthenticationService.getAuthToken").info({ message: "Before session config check", session });

    // check if authentication configuration settings have been
    // retrieved from the api, if not request them
    if (!session.config) {
      let response = await axios.request({
        baseURL: config.apiEndpoints.base,
        url: config.apiEndpoints.identityConfig,
        params: { authInstance: config.authInstance },
      });
      baseContainer.store.dispatch(setAuthConfig(response.data));
      session = baseContainer.getStoreState().session;
    }

    Logger.of("App.AuthenticationService.getAuthToken").info({ message: "After session config check", session });



    // request/refresh Auth0 token silently
    this.token = await this.getAccessToken();
    Logger.of("App.AuthenticationService.getAuthToken.token").info({ token: this.token });

    // retrieve authorization token from localstorage
    this.authToken = this.getTokenFromStorage();
    Logger.of("App.AuthenticationService.getAuthToken.authToken").info({ authToken: this.authToken });

    // validate authotization token
    if (!this.authToken || this.authTokenExpired()) {
      Logger.of("App.AuthenticationService.getAuthToken").info({
        authToken: this.authToken,
        authTokenExpired: this.authTokenExpired(), isRequestingToken: this.isRequestingToken()
      });
      // if authotization token is not valid then check if
      // another thread/tab is attempting to refresh it. If
      // so wait for the token to be refreshed and retrieve
      // the new one from the localstorage
      if (this.isRequestingToken()) {
        Logger.of("App.AuthenticationService.getAuthToken").info({ message: "Waiting for token" });
        await this.#waitForToken();

        this.authToken = this.getTokenFromStorage();
        if (!this.authToken) {
          Logger.of("App.AuthenticationService.apiAuthToken").error(
            "Auth token is null"
          );
          throw new ApiAuthTokenError();
        }
      } else {
        Logger.of("App.AuthenticationService.getAuthToken").info({ message: "Requesting token" });
        // when attempting to refresh the authorization token
        // flag the attempt in localstorage in order to prevent
        // the rest of the threads/tabs to do the same and as a
        // result invalidate the token
        this.setRequestingToken(true);
        try {
          let response = await axios.request({
            baseURL: config.apiEndpoints.base,
            url: config.apiEndpoints.identityCurrent,
            params: {
              moduleName: config.appName,
            },
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          });
          Logger.of("App.AuthenticationService.getAuthToken.axios.request").info({ response });
          this.authToken = response.data.token;
          this.saveTokenToStorage(this.authToken);

          //if a tenantId exists in localstorage then extend the token.
          let tenantId = this.getSelectedTenant();
          if (tenantId) {
            this.authToken = await this.extendToken(tenantId);
          }

          this.setRequestingToken(false);
        } catch (err) {
          // The failed response will be handled from the axios interceptor
          // that triggers postLogout process first. This should not execute
          // in most cases, unless the redirect to base URL takes too long.
          Logger.of("App.AuthenticationService.requestToken").error(err);
          this.removeTokenFromStorage();
          this.setRequestingToken(false);
          throw err;
        }
      }
    }

    // initialize user claims
    this.setPermissions();

    return { token: this.token, authToken: this.authToken };
  }

  // Get Access Token from Auth0
  async getAccessToken(scope) {
    try {
      const authResult = await this.auth0Instance.getAccessTokenSilently({
        detailedResponse: true,
      });
      // in case no token value is returned
      if (!authResult || !authResult.access_token) {
        Logger.of("App.AuthenticationService.getAccessToken").error(
          "Missing Auth0 token"
        );
        throw new Auth0TokenError();
      }

      return authResult.access_token;
      // in case silent token aquisition fails
    } catch (err) {
      Logger.of("App.AuthenticationService.getAccessToken").error(err);

      throw err;
    }
  }

  postLogout(forceClient) {
    if (forceClient) {
      this.auth0Instance.logout();
      this.#clearSessionAndLocalStorage();
    } else {
      this.#clearSessionAndLocalStorage();
       
      window.location.href = "/";
    }
  }

  clearStorages(logout = false) {
    if (this.isEywpMfe) return;
    
    // Before clearing local and session storage, save the cookies accepted status
    const hasAcceptedCookies = baseContainer.cookiesService.getCookiesStatus();
    localStorage.clear();
    sessionStorage.clear();
    baseContainer.cookiesService.setCookiesStatus(hasAcceptedCookies);
  }

  async #waitForToken(counter) {
    var _this = this;

    if (!counter) counter = 1;

    return new Promise((resolve) =>
      setTimeout(async () => {
        if (_this.isRequestingToken() && counter < 20) {
          await _this.#waitForToken(counter + 1);
        }

        resolve();
      }, 100)
    );
  }

  /**
   * Clear local storage when log out is initiated
   */
  #clearSessionAndLocalStorage() {
    baseContainer.persistor.pause();
    // Write any pending state to local storage before clearing it
    baseContainer.persistor.flush().then(() => this.clearStorages(this.#isLoggingOut()));
  }

  #isLoggingOut() {
    let logout = sessionStorage.getItem("logout");
    return logout !== null && logout === "true";
  }

}
